import type {
  ChatView,
  ContactView,
  DashboardOverview,
  MessageView,
  QrView,
  RuntimeLogView,
  SessionView,
  WebhookDeliveryView,
} from './payloads';
import type { SessionStatus } from './enums';

/**
 * Socket.io event names. The dashboard subscribes per session by joining the
 * room `session:<id>`; account-wide events land in `user:<id>`.
 */
export const WsEvent = {
  QR_UPDATED: 'qr.updated',
  SESSION_CONNECTED: 'session.connected',
  SESSION_DISCONNECTED: 'session.disconnected',
  SESSION_STATUS: 'status.changed',
  MESSAGE_NEW: 'message.new',
  MESSAGE_UPDATED: 'message.updated',
  MESSAGE_DELETED: 'message.deleted',
  TYPING: 'typing',
  CHAT_UPDATED: 'chat.updated',
  CONTACT_UPDATED: 'contact.updated',
  KEYWORD_HIT: 'keyword.hit',
  WEBHOOK_DELIVERY: 'webhook.delivery',
  LOG: 'log.line',
  DASHBOARD_TICK: 'dashboard.tick',
} as const;
export type WsEvent = (typeof WsEvent)[keyof typeof WsEvent];

/** Client -> server. */
export const WsCommand = {
  SUBSCRIBE_SESSION: 'session.subscribe',
  UNSUBSCRIBE_SESSION: 'session.unsubscribe',
  SUBSCRIBE_LOGS: 'log.subscribe',
} as const;
export type WsCommand = (typeof WsCommand)[keyof typeof WsCommand];

export interface WsEventMap {
  [WsEvent.QR_UPDATED]: { sessionId: string; qr: QrView };
  [WsEvent.SESSION_CONNECTED]: { sessionId: string; session: SessionView };
  [WsEvent.SESSION_DISCONNECTED]: { sessionId: string; reason: string };
  [WsEvent.SESSION_STATUS]: {
    sessionId: string;
    status: SessionStatus;
    previous: SessionStatus;
    detail?: string;
  };
  [WsEvent.MESSAGE_NEW]: { sessionId: string; message: MessageView };
  [WsEvent.MESSAGE_UPDATED]: { sessionId: string; message: MessageView };
  [WsEvent.MESSAGE_DELETED]: { sessionId: string; messageId: string };
  [WsEvent.TYPING]: { sessionId: string; chatId: string; senderName: string | null };
  [WsEvent.CHAT_UPDATED]: { sessionId: string; chat: ChatView };
  [WsEvent.CONTACT_UPDATED]: { sessionId: string; contact: ContactView };
  [WsEvent.KEYWORD_HIT]: {
    sessionId: string;
    keyword: string;
    messageId: string;
    excerpt: string;
  };
  [WsEvent.WEBHOOK_DELIVERY]: { delivery: WebhookDeliveryView };
  [WsEvent.LOG]: RuntimeLogView;
  [WsEvent.DASHBOARD_TICK]: DashboardOverview;
}

/**
 * Webhook event names. Deliberately a superset of the socket names so a single
 * subscription list can drive both surfaces.
 */
export const WEBHOOK_EVENTS = [
  'message.new',
  'message.updated',
  'message.deleted',
  'session.connected',
  'session.disconnected',
  'session.qr',
  'keyword.hit',
  'chat.updated',
  /**
   * A bank alert that a rule read into a transaction. Fires on the first read
   * and again whenever a later read of the same message changes what was
   * stored - LINE draws a Flex bubble lazily, so the fuller version with the
   * counterparty on it often arrives a moment after the summary.
   */
  'bank.transaction',
] as const;
export type WebhookEventName = (typeof WEBHOOK_EVENTS)[number];
